import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Divider,
  IconButton,
  Paper,
  Grid,
  Alert,
  Chip,
  Card
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import styles from '../../../styles/purchaseInvoices/approvalPackages/AllocationKeyModalStyles';

const AllocationKeyModal = ({
  open,
  onClose,
  onSubmit,
  allocationKeys = [],
  companies = [], 
  profitCenters = [],
  isLoading,
  error,
  defaultYear,
  isSubmitting = false
}) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const [inputValue, setInputValue] = useState(defaultYear || '');
  const [showPercentageMode, setShowPercentageMode] = useState(false);
  const [percentageAllocations, setPercentageAllocations] = useState([
    { id: 1, companyId: null, profitCenterId: null, quantity: '', percentage: '' }
  ]);
  
  const [confirmedAllocations, setConfirmedAllocations] = useState(null);
  const [isConfirmationStep, setIsConfirmationStep] = useState(false);
  
  useEffect(() => {
    if (open) {
      setSelectedKey(null);
      setInputValue(defaultYear || '');
      setShowPercentageMode(false);
      setPercentageAllocations([
        { id: 1, companyId: null, profitCenterId: null, quantity: '', percentage: '' }
      ]);
      setConfirmedAllocations(null);
      setIsConfirmationStep(false);
    }
  }, [open, defaultYear]);


  const calculatePercentagesFromQuantities = React.useCallback((allocations) => {
    const totalQuantity = allocations.reduce((sum, allocation) => {
      const quantity = parseFloat(allocation.quantity) || 0;
      return sum + quantity;
    }, 0);

    if (totalQuantity === 0) {
      return allocations.map(allocation => ({ ...allocation, percentage: '' }));
    }

    return allocations.map(allocation => {
      const quantity = parseFloat(allocation.quantity) || 0;
      const percentage = (quantity / totalQuantity) * 100;
      return { ...allocation, percentage: percentage.toFixed(1) };
    });
  }, []);

  const options = React.useMemo(() => {
    const specialOptions = [
      {
        id: 'no_allocation_key',
        name: 'ללא מפתח העמסה',
        splitKeyName: null,
        isNoKey: true
      },
      {
        id: 'company_allocation',
        name: 'חלוקה לפי חברות',
        splitKeyName: null,
        isCompanyAllocation: true
      }
    ];

    if (!Array.isArray(allocationKeys)) return specialOptions;

    const filtered = allocationKeys.filter(key => {
      if (!key || !key.name) return false;
      return key.name.toLowerCase().includes(inputValue.toLowerCase());
    });

    return [...specialOptions, ...filtered];
  }, [allocationKeys, inputValue]);

  const totalQuantity = percentageAllocations.reduce((sum, allocation) => {
    const quantity = parseFloat(allocation.quantity) || 0;
    return sum + quantity;
  }, 0);

  const totalPercentage = percentageAllocations.reduce((sum, allocation) => {
    const percentage = parseFloat(allocation.percentage) || 0;
    return sum + percentage;
  }, 0);

  const isValidPercentages = totalQuantity > 0 && percentageAllocations.every(
    allocation => allocation.companyId && allocation.quantity && parseFloat(allocation.quantity) > 0
  );

  const handleKeySelection = (newValue) => {
    setSelectedKey(newValue);
    setShowPercentageMode(newValue && newValue.isCompanyAllocation);
  };

  const handleAddAllocation = () => {
    const newId = Math.max(...percentageAllocations.map(a => a.id)) + 1;
    setPercentageAllocations([
      ...percentageAllocations,
      { id: newId, companyId: null, profitCenterId: null, quantity: '', percentage: '' }
    ]);
  };

  const handleRemoveAllocation = (id) => {
    if (percentageAllocations.length > 1) {
      setPercentageAllocations(percentageAllocations.filter(allocation => allocation.id !== id));
    }
  };

  const handleAllocationChange = (id, field, value) => {
    const updatedAllocations = percentageAllocations.map(allocation => 
      allocation.id === id 
        ? { ...allocation, [field]: value }
        : allocation
    );
    
    if (field === 'quantity') {
      const allocationsWithCalculatedPercentages = calculatePercentagesFromQuantities(updatedAllocations);
      setPercentageAllocations(allocationsWithCalculatedPercentages);
    } else {
      setPercentageAllocations(updatedAllocations);
    }
  };

  const handleEditConfirmedAllocations = () => {
    setIsConfirmationStep(false);
    setShowPercentageMode(true);
    setSelectedKey({
      id: 'company_allocation',
      name: 'חלוקה לפי חברות',
      splitKeyName: null,
      isCompanyAllocation: true
    });
    setPercentageAllocations(confirmedAllocations.allocations.map(allocation => ({
      id: allocation.id,
      companyId: companies.find(c => c.companyNumber === allocation.companyId)?.id || allocation.companyId,
      profitCenterId: allocation.profitCenterId,
      quantity: allocation.quantity || '',
      percentage: allocation.percentage
    })));
  };

  const handleSubmit = () => {
    if (selectedKey) {
      if (selectedKey.isCompanyAllocation && showPercentageMode && !isConfirmationStep) {
        const allocationData = {
          type: 'percentage_allocation',
          allocations: percentageAllocations.map(allocation => {
            const selectedCompany = companies.find(c => c.id === allocation.companyId);
            return {
              id: allocation.id,
              companyId: selectedCompany?.companyNumber || allocation.companyId, 
              companyName: selectedCompany?.name || '', 
              profitCenterId: allocation.profitCenterId || null, 
              quantity: parseFloat(allocation.quantity) || 0,
              percentage: parseFloat(allocation.percentage)
            };
          })
        };
        
        setConfirmedAllocations(allocationData);
        setIsConfirmationStep(true);
        setShowPercentageMode(false);
        setSelectedKey({
          id: 'company_allocation_confirmed',
          name: 'חלוקה לפי חברות',
          splitKeyName: null,
          isCompanyAllocation: true
        });
      } else {
        if (confirmedAllocations) {
          onSubmit(confirmedAllocations);
        } else if (selectedKey.isNoKey) {
          onSubmit(null);
        } else {
          const keyToSend = {
            id: selectedKey.id,
            name: selectedKey.name, 
            splitKeyName: selectedKey.splitKeyName  
          };
          onSubmit(keyToSend);
        }
      }
    } else {
      onSubmit(null);
    }
  };

  const canSubmit = selectedKey && (
    !showPercentageMode || isValidPercentages || isConfirmationStep
  );

  const getDialogTitle = () => {
    if (isConfirmationStep) {
      return 'אישור סופי - מפתח העמסה';
    }
    return 'בחירת מפתח העמסה';
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) {
      return isConfirmationStep ? 'מאשר ויוצר חשבונית...' : 'שומר חלוקה...';
    }
    if (isConfirmationStep) {
      return 'אישור סופי ויצירת חשבונית';
    }
    if (selectedKey && selectedKey.isCompanyAllocation && showPercentageMode) {
      return 'שמור חלוקה והמשך';
    }
    return 'אישור';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      dir="rtl"
    >
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      
      <DialogContent>
        {error ? (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        ) : (
          <>
            {isConfirmationStep && confirmedAllocations && (
              <Card sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: 'success.50', 
                border: '1px solid', 
                borderColor: 'success.200',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.dark' }}>
                    <CheckCircleIcon fontSize="small" />
                    חלוקה לפי חברות
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleEditConfirmedAllocations}
                    sx={{ fontSize: '0.75rem', color: 'success.dark' }}
                  >
                    ערוך
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {confirmedAllocations.allocations.map((allocation, index) => (
                    <Chip
                      key={allocation.id || index}
                      size="medium"
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {allocation.companyName}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            •
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {allocation.quantity} שעות
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            (
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {allocation.percentage}%
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            )
                          </Typography>
                        </Box>
                      }
                      color="success"
                      variant="outlined"
                      sx={{ 
                        '& .MuiChip-label': { 
                          px: 2, 
                          py: 1 
                        } 
                      }}
                    />
                  ))}
                </Box>
              </Card>
            )}

            {!isConfirmationStep && (
              <Box sx={styles.autocompleteContainer}>
                <Autocomplete
                  id="allocation-key-combo"
                  options={options}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedKey}
                  onChange={(event, newValue) => handleKeySelection(newValue)}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  loading={isLoading}
                  fullWidth
                  freeSolo
                  filterOptions={(opts, state) => {
                    const specialOptions = opts.filter(o => o.isNoKey || o.isCompanyAllocation);
                    const filtered = opts.filter(o =>
                      !o.isNoKey && !o.isCompanyAllocation &&
                      o.name &&
                      o.name.toLowerCase().includes(state.inputValue.toLowerCase())
                    );
                    return [...specialOptions, ...filtered];
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="חיפוש מפתח העמסה (לפי שנה / שם)"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <FilterListIcon sx={styles.filterIcon(inputValue)} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} style={styles.optionItem(option.isNoKey || option.isCompanyAllocation)}>
                      {option.isNoKey && (
                        <RemoveCircleIcon sx={{ mr: 1, fontSize: 18, color: 'action.active' }} />
                      )}
                      {option.isCompanyAllocation && (
                        <BusinessIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                      )}
                      {option.name}
                    </li>
                  )}
                />
              </Box>
            )}

            {!showPercentageMode && !isConfirmationStep && (
              <Box sx={styles.resultsText}>
                <Typography variant="caption" color="text.secondary">
                  מציג {options.length - 2} מפתחות 
                  {Array.isArray(allocationKeys) && (options.length - 2) !== allocationKeys.length && 
                    ` מתוך ${allocationKeys.length}`}
                  {inputValue && ` עבור חיפוש "${inputValue}"`}
                </Typography>
              </Box>
            )}

            {showPercentageMode && !isConfirmationStep && (
              <>
                <Divider sx={styles.percentageModeSection} />
                <Typography variant="h6" gutterBottom>
                  חלוקה לפי כמות שעות
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  הגדר כמות שעות לחברה או מספר חברות - האחוזים יחושבו אוטומטית
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Alert 
                    severity={totalQuantity > 0 && totalPercentage === 100 ? "success" : "info"}
                    sx={styles.allocationAlert}
                    icon={totalQuantity > 0 && totalPercentage === 100 ? <CheckCircleIcon /> : undefined}
                  >
                    <Box sx={styles.percentageHeader}>
                      <Typography variant="body2" fontWeight="medium">
                        סך כל השעות: {totalQuantity} שעות
                      </Typography>
                      {totalQuantity > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          (100% מחושב אוטומטית)
                        </Typography>
                      )}
                    </Box>
                    <Box sx={styles.progressBarContainer}>
                      <Box sx={styles.progressBarBackground}>
                        <Box sx={styles.progressBarFill(100)} />
                        <Typography
                          variant="caption"
                          sx={styles.progressBarText}
                        >
                          {totalQuantity > 0 ? '100%' : '0%'}
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                </Box>

                <Box sx={styles.allocationsContainer}>
                  {percentageAllocations.map((allocation, index) => (
                    <Paper 
                      key={allocation.id} 
                      sx={styles.allocationPaper}
                      elevation={1}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <Autocomplete
                            options={companies}
                            getOptionLabel={(option) => option.name || ''}
                            value={companies.find(c => c.id === allocation.companyId) || null}
                            onChange={(event, newValue) => 
                              handleAllocationChange(allocation.id, 'companyId', newValue?.id || null)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="חברה"
                                size="small"
                                required
                              />
                            )}
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={8} sm={3}>
                          <TextField
                            label="כמות שעות"
                            type="number"
                            value={allocation.quantity}
                            onChange={(e) => 
                              handleAllocationChange(allocation.id, 'quantity', e.target.value)
                            }
                            inputProps={{
                              min: 0,
                              step: 0.5
                            }}
                            size="small"
                            required
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'success.50',
                            border: '1px solid',
                            borderColor: 'success.200',
                            borderRadius: 1,
                            p: 1,
                            minHeight: '40px'
                          }}>
                            <Typography 
                              variant="body2" 
                              fontWeight="bold" 
                              color="success.dark"
                            >
                              {allocation.percentage ? `${allocation.percentage}%` : '0%'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4} sm={1}>
                          <IconButton
                            onClick={() => handleRemoveAllocation(allocation.id)}
                            disabled={percentageAllocations.length <= 1}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddAllocation}
                  variant="outlined"
                  sx={styles.addAllocationButton}
                >
                  הוסף חלוקה
                </Button>
              </>
            )}
            {isConfirmationStep && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  החלוקה שלעיל תיושם על החשבונית. לחיצה על "אישור סופי" תקלוט את החשבונית במערכת פריוריטי.
                </Typography>
              </Alert>
            )}
          </>
        )}
        
        {isSubmitting && (
          <Box sx={styles.submittingIndicator}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="primary"
          disabled={isSubmitting}
        >
          ביטול
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!canSubmit || isLoading || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={styles.submitButton} />
              {getSubmitButtonText()}
            </>
          ) : (
            getSubmitButtonText()
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllocationKeyModal;